import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Coins as CoinsIcon, Gift, Banknote, Plus, Sparkles, ArrowDown, ArrowUp, Rocket, ShieldAlert,
  Loader2, CheckCircle2, XCircle, Building2, ExternalLink, ShieldCheck,
} from "lucide-react";

export default function Coins() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [swipe, setSwipe] = useState(null);   // /swipe/status — for Boost Pack perk
  const [showBuy, setShowBuy] = useState(false);
  const [buyingPack, setBuyingPack] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const [showCash, setShowCash] = useState(false);
  const [giftForm, setGiftForm] = useState({ to_user_id: "", coins: 10, note: "" });
  const [cashForm, setCashForm] = useState({ coins: 100, bank_name: "", iban: "", holder: "", country: "" });
  const [paying, setPaying] = useState(null);   // bundle id while creating session
  const [polling, setPolling] = useState(null); // {status: 'checking'|'paid'|'expired'|'error'}
  const [connect, setConnect] = useState(null); // {available, has_account, status, payouts_enabled, ...}
  const [connectBusy, setConnectBusy] = useState(false);

  const loadConnect = useCallback(async () => {
    try {
      const { data: s } = await api.get("/payouts/connect/status");
      setConnect(s);
    } catch {
      setConnect({ available: false });
    }
  }, []);

  const load = useCallback(async () => {
    const [{ data: w }, { data: b }, swipeRes] = await Promise.all([
      api.get("/coins/me"),
      api.get("/payments/coin-bundles"),
      api.get("/swipe/status").catch(() => ({ data: null })),
    ]);
    setData(w); setBundles(b); setSwipe(swipeRes.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadConnect(); }, [loadConnect]);

  // When the user returns from Stripe Connect onboarding (?connect=return) we
  // ping the backend to resync the account status so the UI reflects reality
  // immediately, without waiting for the webhook.
  useEffect(() => {
    const c = params.get("connect");
    if (!c) return;
    (async () => {
      try { await api.post("/payouts/connect/refresh-status"); } catch (_) { /* ignore */ }
      await loadConnect();
      await refresh();
      const next = new URLSearchParams(params);
      next.delete("connect");
      setParams(next, { replace: true });
      if (c === "return") toast.success(t("connect.refreshed", { defaultValue: "Connect account updated" }));
    })();
   
  }, [params.get("connect")]);

  const startConnectOnboard = async () => {
    if (connectBusy) return;
    setConnectBusy(true);
    try {
      const { data: r } = await api.post("/payouts/connect/onboard");
      window.location.href = r.url;
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setConnectBusy(false);
    }
  };

  // Detect return from Stripe (?session_id=...) and poll status
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    let attempt = 0;
    const MAX = 10;
    setPolling({ status: "checking" });
    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const { data: tx } = await api.get(`/payments/status/${sid}`);
        if (tx.payment_status === "paid") {
          setPolling({ status: "paid", tx });
          toast.success(t("coinsPage.paymentReceived"));
          await refresh();
          await load();
          // Clear session_id from URL after a moment
          setTimeout(() => setParams({}), 1500);
          return;
        }
        if (tx.payment_status === "expired" || tx.status === "expired") {
          setPolling({ status: "expired" });
          return;
        }
        if (attempt >= MAX) { setPolling({ status: "error" }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempt >= MAX) { setPolling({ status: "error" }); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
   
  }, [params.get("session_id")]);

  const buy = async (bundleId) => {
    setPaying(bundleId);
    try {
      const { data } = await api.post("/payments/checkout/coins", {
        bundle: bundleId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;  // off to Stripe
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setPaying(null);
    }
  };

  const gift = async () => {
    try {
      await api.post("/coins/gift", { ...giftForm, coins: Number(giftForm.coins) });
      toast.success(t("coinsPage.giftSent"));
      setShowGift(false); setGiftForm({ to_user_id: "", coins: 10, note: "" });
      await refresh(); load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  const cashout = async () => {
    try {
      const { data } = await api.post("/coins/cashout", { ...cashForm, coins: Number(cashForm.coins) });
      toast.success(t("coinsPage.cashRequested", { net: data.net_eur }));
      setShowCash(false);
      await refresh(); load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  const buyBoostPack = async () => {
    if (buyingPack) return;
    if (!swipe?.is_vip) { toast.error(t("perks.vipOnly")); return; }
    const cost = swipe.boost_pack_cost ?? 500;
    const size = swipe.boost_pack_size ?? 3;
    if (!window.confirm(t("perks.boostPackConfirm", { size, cost }))) return;
    setBuyingPack(true);
    try {
      const { data } = await api.post("/swipe/boost-pack");
      toast.success(t("perks.boostPackBought", { size: data.credits_added }));
      await refresh(); await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "not_enough_coins") toast.error(t("perks.notEnoughCoins"));
      else if (code === "vip_required") toast.error(t("perks.vipOnly"));
      else toast.error(fmtError(detail) || e.message);
    } finally { setBuyingPack(false); }
  };

  if (!data) return <div className="p-8 text-center">{t("common.loading")}</div>;

  const eurValue = (data.balance * data.coin_price_eur).toFixed(2);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="back-btn"><ArrowLeft className="w-4 h-4" strokeWidth={2.5}/> {t("common.back")}</button>

      {/* Polling banner */}
      {polling && polling.status !== "paid" && (
        <div className={`card-nb p-4 mb-5 flex items-center gap-3 ${polling.status === "error" || polling.status === "expired" ? "bg-[#FFE6E0]" : "bg-[#FFF1D6]"}`} data-testid="polling-banner">
          {polling.status === "checking" && <><Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5}/> <span>{t("coinsPage.confirming")}</span></>}
          {polling.status === "expired" && <><XCircle className="w-5 h-5" strokeWidth={2.5}/> <span>{t("coinsPage.expired")}</span></>}
          {polling.status === "error" && <><XCircle className="w-5 h-5" strokeWidth={2.5}/> <span>{t("coinsPage.failed")}</span></>}
        </div>
      )}
      {polling?.status === "paid" && (
        <div className="card-nb p-4 mb-5 flex items-center gap-3 bg-[#DDF5EA]" data-testid="paid-banner">
          <CheckCircle2 className="w-5 h-5 text-[#0F9D8F]" strokeWidth={2.5}/>
          <span className="font-display font-semibold">{t("coinsPage.confirmed")}</span>
        </div>
      )}

      {/* Balance hero */}
      <div className="card-nb p-7 mb-5 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0F1115 0%, #1F2228 60%, #14C8B0 100%)" }} data-testid="coins-hero">
        <div className="absolute -right-6 -top-6 w-40 h-40 rounded-full bg-[#F9C349]/40 blur-3xl"/>
        <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-white/70">{t("coins.balance")}</p>
        <div className="flex items-baseline gap-3 mt-2">
          <span className="font-display font-black text-6xl tracking-tight">{data.balance}</span>
          <CoinsIcon className="w-8 h-8 text-[#F9C349]" strokeWidth={2.5} fill="#F9C349"/>
        </div>
        <p className="text-white/80 mt-1">≈ €{eurValue} EUR · 1 coin = €{data.coin_price_eur}</p>
        <div className="flex gap-2 mt-5 flex-wrap">
          <button onClick={() => setShowBuy(true)} className="btn-nb btn-nb-warn" data-testid="buy-coins-btn"><Plus className="w-4 h-4" strokeWidth={2.5}/> {t("coins.buy")}</button>
          <button onClick={() => setShowGift(true)} className="btn-nb" data-testid="gift-btn"><Gift className="w-4 h-4" strokeWidth={2.5}/> {t("coins.gift")}</button>
          <button
            onClick={() => {
              if (!user?.verified) {
                toast.error("You must be verified to cash out. Please submit your ID verification first.");
              } else {
                setShowCash(true);
              }
            }}
            className="btn-nb"
            data-testid="cashout-btn"
          >
            <Banknote className="w-4 h-4" strokeWidth={2.5}/> {t("coinsPage.cashOut")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="card-nb p-3 text-center bg-[#FFF1D6]"><Rocket className="w-5 h-5 mx-auto mb-1" strokeWidth={2.25}/><div className="text-xs text-[#4A4F58]">{t("coinsPage.boost")}</div><div className="font-display font-extrabold">10 🪙</div></div>
        <div className="card-nb p-3 text-center bg-[#DDF5EA]"><Gift className="w-5 h-5 mx-auto mb-1" strokeWidth={2.25}/><div className="text-xs text-[#4A4F58]">{t("coinsPage.gift")}</div><div className="font-display font-extrabold">{t("coinsPage.free")}</div></div>
      </div>

      {/* Perks — spend T-Coins on in-app boosts. Boost Pack requires VIP. */}
      {swipe && (
        <div className="card-nb p-5 mb-5 bg-white" data-testid="perks-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-extrabold text-lg tracking-tight flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#B49CFF]" strokeWidth={2.5}/> {t("perks.title")}
            </h3>
            {swipe.boost_credits > 0 && (
              <span className="bg-[#B49CFF] text-white text-xs font-black px-2 py-1 rounded-full uppercase tracking-wide" data-testid="boost-credits-badge">
                {t("perks.creditsHeld", { n: swipe.boost_credits })}
              </span>
            )}
          </div>

          <div className="rounded-2xl border-2 border-[#121212] p-4 bg-gradient-to-br from-[#FFF3CD] to-[#FFE9B0] relative overflow-hidden" data-testid="boost-pack-card">
            <div className="absolute -right-3 -top-3 bg-[#FF4E5B] text-white text-[10px] font-black px-3 py-1 rounded-full rotate-12 border-2 border-[#121212] shadow-[2px_2px_0_#121212]">
              {t("perks.save17")}
            </div>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#F9C349] to-[#FFB347] border-2 border-[#121212] flex items-center justify-center shrink-0">
                <Rocket className="w-6 h-6 text-[#4A3000]" strokeWidth={2.5}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-lg leading-tight">{t("perks.boostPackName", { size: swipe.boost_pack_size ?? 3 })}</div>
                <p className="text-xs text-[#4A3000] mt-0.5">{t("perks.boostPackDesc")}</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="font-display font-black text-2xl">{swipe.boost_pack_cost ?? 500} 🪙</span>
                  <span className="text-xs text-[#7C828D] line-through">{(swipe.boost_cost ?? 200) * (swipe.boost_pack_size ?? 3)} 🪙</span>
                </div>
              </div>
            </div>
            <button
              onClick={buyBoostPack}
              disabled={buyingPack || !swipe.is_vip || (data?.balance ?? 0) < (swipe.boost_pack_cost ?? 500)}
              data-testid="buy-boost-pack-btn"
              className="w-full mt-3 bg-[#00CED1] text-black font-black py-3 rounded-2xl border-2 border-[#121212] shadow-[3px_3px_0_#121212] active:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
            >
              {buyingPack ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4" strokeWidth={3}/>}
              {!swipe.is_vip
                ? t("perks.vipRequired")
                : (data?.balance ?? 0) < (swipe.boost_pack_cost ?? 500)
                  ? t("perks.notEnoughShort")
                  : t("perks.buyBoostPack")}
            </button>
          </div>
        </div>
      )}

      {/* Stripe Connect status & onboarding (automated cashouts) */}
      {connect?.available && (
        <div
          className={`card-nb p-5 mb-5 ${connect.status === "verified" ? "bg-[#DDF5EA]" : "bg-[#FFF1D6]"}`}
          data-testid="connect-card"
        >
          <div className="flex items-start gap-3">
            {connect.status === "verified"
              ? <ShieldCheck className="w-7 h-7 text-[#0F9D8F] shrink-0" strokeWidth={2.5}/>
              : <Building2 className="w-7 h-7 shrink-0" strokeWidth={2.5}/>}
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-extrabold text-lg tracking-tight">
                {connect.status === "verified"
                  ? t("connect.verifiedTitle", { defaultValue: "Instant cashouts enabled" })
                  : connect.status === "in_progress"
                    ? t("connect.inProgressTitle", { defaultValue: "Finish your Stripe onboarding" })
                    : t("connect.startTitle", { defaultValue: "Get instant cashouts to your bank" })}
              </h3>
              <p className="text-xs text-[#4A3000] mt-1">
                {connect.status === "verified"
                  ? t("connect.verifiedBody", { defaultValue: "Your Stripe account is verified. Cashouts are sent automatically." })
                  : connect.status === "in_progress"
                    ? t("connect.inProgressBody", { defaultValue: "Stripe still needs a few details before payouts can be enabled." })
                    : t("connect.startBody", { defaultValue: "Link a Stripe Express account once — afterwards every cashout is paid automatically to your bank, with no manual processing." })}
              </p>
              {connect.status !== "verified" && (
                <button
                  onClick={startConnectOnboard}
                  disabled={connectBusy}
                  data-testid="connect-onboard-btn"
                  className="btn-nb btn-nb-warn mt-3"
                >
                  {connectBusy ? (
                    <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> {t("connect.opening", { defaultValue: "Opening Stripe…" })}</>
                  ) : (
                    <>{connect.has_account
                        ? t("connect.continue", { defaultValue: "Continue onboarding" })
                        : t("connect.start", { defaultValue: "Set up Stripe" })}
                      <ExternalLink className="w-4 h-4" strokeWidth={2.5}/></>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card-nb p-5 bg-white">
        <h3 className="font-display font-extrabold text-lg mb-3 tracking-tight">{t("coinsPage.history")}</h3>
        {data.history.length === 0 ? (
          <p className="text-[#8A8F98] text-sm">{t("coinsPage.noTx")}</p>
        ) : (
          <ul className="divide-y divide-[#E8E5DD]">
            {data.history.map((tx) => (
              <li key={tx.id} className="py-3 flex items-center gap-3" data-testid={`tx-${tx.id}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${tx.coins > 0 ? "bg-[#DDF5EA]" : "bg-[#FFE6E0]"}`}>
                  {tx.coins > 0 ? <ArrowDown className="w-4 h-4 text-[#0F9D8F]" strokeWidth={2.5}/> : <ArrowUp className="w-4 h-4 text-[#FF7A6B]" strokeWidth={2.5}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-sm truncate">{tx.note || tx.type}</div>
                  <div className="text-[11px] text-[#8A8F98]">{new Date(tx.created_at).toLocaleString()}</div>
                </div>
                <div className={`font-display font-extrabold tracking-tight ${tx.coins > 0 ? "text-[#0F9D8F]" : "text-[#FF7A6B]"}`}>
                  {tx.coins > 0 ? "+" : ""}{tx.coins}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Buy modal */}
      {showBuy && (
        <Modal onClose={() => setShowBuy(false)} title={t("coinsPage.buyTitle")}>
          <p className="text-sm text-[#4A4F58] mb-4">{t("coinsPage.buyBody")}</p>
          {data?.first_purchase_bonus_available && (
            <div className="mb-4 p-3 rounded-2xl bg-[#FFF0C2] border-2 border-[#121212] text-sm font-black flex items-center gap-2" data-testid="first-purchase-bonus">
              🎁 {t("coinsPage.firstPurchaseBonus")}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {bundles.map((b) => {
              const emojis = { starter: "✨", popular: "🔥", pro: "🚀", whale: "🐳" };
              return (
                <button key={b.id} onClick={() => buy(b.id)} disabled={paying === b.id} className="card-nb p-4 text-left hover:-translate-y-0.5 transition-all bg-white disabled:opacity-60" data-testid={`buy-${b.id}`}>
                  <div className="text-2xl">{emojis[b.id] || "💎"}</div>
                  <div className="font-display font-extrabold text-2xl tracking-tight">{b.coins} <span className="text-sm">{t("coinsPage.coinsWord")}</span></div>
                  {data?.first_purchase_bonus_available && (
                    <div className="text-[11px] font-black text-[#0F9D8F]">+{Math.round(b.coins * 0.1)} 🎁</div>
                  )}
                  <div className="text-xs text-[#8A8F98]">{b.label}</div>
                  <div className="font-display font-bold mt-2 bg-gradient-to-r from-[#0F9D8F] to-[#14C8B0] bg-clip-text text-transparent">€{b.price_eur.toFixed(2)}</div>
                  {paying === b.id && <div className="mt-2 text-xs flex items-center gap-1 text-[#4A4F58]"><Loader2 className="w-3 h-3 animate-spin"/> {t("coinsPage.redirecting")}</div>}
                </button>
              );
            })}
          </div>
          <div className="mt-4 p-3 bg-[#FFE6E0] border-2 border-[#FF7A6B] rounded-2xl text-center" data-testid="coins-no-refund-notice">
            <p className="text-[11px] font-display font-extrabold text-[#D14848] leading-tight">
              ⚠️ STRICT NO-REFUND POLICY: All T-Coin purchases are final and non-refundable. Account deletion or profile deactivation will not entitle you to any refund.
            </p>
          </div>
          <p className="text-[10px] text-[#8A8F98] mt-3 text-center">{t("coinsPage.testMode")}</p>
        </Modal>
      )}

      {/* Gift modal */}
      {showGift && (
        <Modal onClose={() => setShowGift(false)} title={t("coinsPage.giftTitle")}>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("coinsPage.recipientId")}</label>
          <input className="input-nb mb-3" placeholder={t("coinsPage.pasteUserId")} value={giftForm.to_user_id} onChange={(e) => setGiftForm({...giftForm, to_user_id: e.target.value})} data-testid="gift-to"/>
          <p className="text-[11px] text-[#8A8F98] mb-3">{t("coinsPage.tipCopyUrl")}</p>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("coinsPage.coinsLabel")}</label>
          <input type="number" min={1} className="input-nb mb-3" value={giftForm.coins} onChange={(e) => setGiftForm({...giftForm, coins: e.target.value})} data-testid="gift-amount"/>
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("coinsPage.noteOptional")}</label>
          <input className="input-nb mb-4" maxLength={120} value={giftForm.note} onChange={(e) => setGiftForm({...giftForm, note: e.target.value})} data-testid="gift-note"/>
          <button onClick={gift} className="btn-nb btn-nb-primary w-full" data-testid="gift-send-btn">{t("coinsPage.sendN", { n: giftForm.coins })}</button>
          <p className="text-[10px] text-[#7C828D] text-center mt-2 italic" data-testid="gift-fee-note">
            * {t("coinsPage.serviceFeeIncluded")}
          </p>
        </Modal>
      )}

      {/* Cashout modal */}
      {showCash && (
        <Modal onClose={() => setShowCash(false)} title={t("coinsPage.cashTitle")}>
          {connect?.payouts_enabled ? (
            <div className="card-nb p-3 mb-3 bg-[#DDF5EA] flex items-start gap-2 text-xs">
              <ShieldCheck className="w-4 h-4 text-[#0F9D8F] mt-0.5 shrink-0" strokeWidth={2.5}/>
              <span>{t("connect.cashoutBadge", { defaultValue: "Stripe is connected — your cashout will be sent automatically." })}</span>
            </div>
          ) : (
            <p className="text-sm text-[#4A4F58] mb-3">
              {t("coinsPage.cashBody")}{" "}
              <button onClick={() => { setShowCash(false); startConnectOnboard(); }} className="underline font-bold" data-testid="cash-connect-link">
                {t("connect.suggest", { defaultValue: "Connect Stripe for instant cashouts" })}
              </button>
            </p>
          )}
          <label className="block text-xs font-display font-bold uppercase tracking-widest mb-1">{t("coinsPage.coinsToCash")}</label>
          <input type="number" min={100} className="input-nb mb-3" value={cashForm.coins} onChange={(e) => setCashForm({...cashForm, coins: e.target.value})} data-testid="cash-coins"/>
          {!connect?.payouts_enabled && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <input className="input-nb" placeholder={t("coinsPage.bankName")} value={cashForm.bank_name} onChange={(e) => setCashForm({...cashForm, bank_name: e.target.value})} data-testid="cash-bank"/>
                <input className="input-nb" placeholder={t("coinsPage.country")} value={cashForm.country} onChange={(e) => setCashForm({...cashForm, country: e.target.value})} data-testid="cash-country"/>
              </div>
              <input className="input-nb mt-2" placeholder={t("coinsPage.holder")} value={cashForm.holder} onChange={(e) => setCashForm({...cashForm, holder: e.target.value})} data-testid="cash-holder"/>
              <input className="input-nb mt-2 mb-4" placeholder={t("coinsPage.iban")} value={cashForm.iban} onChange={(e) => setCashForm({...cashForm, iban: e.target.value})} data-testid="cash-iban"/>
            </>
          )}
          <button onClick={cashout} className="btn-nb btn-nb-primary w-full" data-testid="cash-submit-btn">
            {connect?.payouts_enabled
              ? t("connect.cashoutInstant", { defaultValue: "Send to Stripe instantly" })
              : t("coinsPage.requestCashout")}
          </button>
          <p className="text-[10px] text-[#7C828D] text-center mt-2 italic" data-testid="cashout-fee-note">
            * {t("coinsPage.serviceFeeIncluded")}
          </p>
        </Modal>
      )}
    </div>
  );
}

function Modal({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 bg-[#0F1115]/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="card-nb p-6 max-w-md w-full bg-white animate-pop max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-extrabold text-xl tracking-tight">{title}</h3>
          <button onClick={onClose} className="btn-nb !p-2" data-testid="modal-close"><Plus className="w-4 h-4 rotate-45"/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
